import { useLazyLoadQuery, usePaginationFragment } from "react-relay";
import { graphql } from "relay-runtime";

import { usePopoverPaginationInfiniteScroll } from "@skydio/relay-toolkit";
import { ComboBox, useDebouncedState } from "@skydio/rivet-ui";

import { useFilteredItemWithMemory } from "./useFilteredItemWithMemory";

import type { OptionalSingleSelectProps } from "@skydio/rivet-ui/src/hooks/useMultiSelectListState";
import type { ControllerReleaseMaxFilter_query$key } from "__generated__/ControllerReleaseMaxFilter_query.graphql";
import type { ControllerReleaseMaxFilterQuery } from "__generated__/ControllerReleaseMaxFilterQuery.graphql";
import type { ControllerReleaseMaxFilterRefetchQuery } from "__generated__/ControllerReleaseMaxFilterRefetchQuery.graphql";

const orgNameUuidFragment = graphql`
  fragment ControllerReleaseMaxFilter_query on Query
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int", defaultValue: 10 }
    maxVersion: { type: "String" }
  )
  @refetchable(queryName: "ControllerReleaseMaxFilterRefetchQuery") {
    controllerReleases(after: $cursor, first: $count, versioned: true, maxVersion: $maxVersion)
      @connection(key: "ControllerReleaseMaxFilter_controllerReleases") {
      totalCount
      edges {
        node {
          uuid
          versionString
          releaseKey
        }
      }
    }
  }
`;

const controllerReleaseMaxFilterQuery = graphql`
  query ControllerReleaseMaxFilterQuery {
    ...ControllerReleaseMaxFilter_query
  }
`;

export type ControllerReleaseMaxSelectFilterProps = OptionalSingleSelectProps;
const ControllerReleaseMaxSelectFilter = (props: ControllerReleaseMaxSelectFilterProps) => {
  const [inputValue, setMaxVersion, { debouncedValue: maxVersion }] = useDebouncedState("");
  const data = useLazyLoadQuery<ControllerReleaseMaxFilterQuery>(
    controllerReleaseMaxFilterQuery,
    {}
  );

  const paginationQuery = usePaginationFragment<
    ControllerReleaseMaxFilterRefetchQuery,
    ControllerReleaseMaxFilter_query$key
  >(orgNameUuidFragment, data);

  const [infiniteScrollProps, currentItems] = usePopoverPaginationInfiniteScroll(
    paginationQuery,
    data => data.controllerReleases,
    10,
    { maxVersion }
  );

  const itemProps = useFilteredItemWithMemory({
    currentItems,
    selectedKey: props.selectedKey ?? null,
    getKey: controllerRelease => controllerRelease.uuid,
  });

  return (
    <ComboBox
      placeholder="Max Version"
      inputValue={inputValue}
      onInputChange={setMaxVersion}
      selectedKey={props.selectedKey}
      onSingleSelectionChange={props.onSingleSelectionChange}
      aria-label="Max Version"
      {...itemProps}
      {...infiniteScrollProps}
    >
      {controllerRelease => (
        <ComboBox.Item
          key={controllerRelease.uuid}
          description={controllerRelease.releaseKey}
          aria-label="controllerRelease.uuid"
        >
          {controllerRelease.versionString}
        </ComboBox.Item>
      )}
    </ComboBox>
  );
};

export default ControllerReleaseMaxSelectFilter;
